import React, { createContext, useContext, useMemo, useEffect } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRetailer } from "../hooks/useRetailer";
import { useFlatFavoritesWithRetailer } from "../hooks/useFavorites";
import { useLocation } from "react-router-dom";
import { useUserContext } from "./UserContext";
import { isFreeTier } from "../utils/userUtils";
import { isOnlineOnlyRetailer } from "../config/retailers";

interface FavoritesContextType {
  favoriteUPCs: string[];
  isLoading: boolean;
  error: Error | null;
  isFavorite: (upc: string) => boolean;
  refetch: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(
  undefined
);

export const FavoritesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const retailer = useRetailer();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const isFreeUser = isFreeTier(user);

  // Ensure retailer is valid, default to walmart if not
  const validRetailer = retailer || "walmart";
  const allowFreeOnline =
    new URLSearchParams(location.search).get("storeId") === "online" ||
    isOnlineOnlyRetailer(validRetailer);

  // When retailer changes, invalidate old queries to ensure fresh data
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["favorites", "list"] });
  }, [validRetailer, queryClient]);

  const { favorites, isLoading, error, refetch } = useFlatFavoritesWithRetailer(
    100,
    validRetailer,
    true,
    { allowFreeOnline, enabled: !isFreeUser }
  ); // basic=true for lightweight UPC list

  // Extract UPCs from favorites (when basic=true, favorites will just be {upc: string} objects)
  const favoriteUPCs = useMemo(() => {
    return favorites.map((fav: any) => fav.upc);
  }, [favorites]);

  // Create a Set for O(1) lookup
  const favoritesSet = useMemo(() => {
    return new Set(favoriteUPCs);
  }, [favoriteUPCs]);

  // Function to check if a UPC is favorited
  const isFavorite = (upc: string): boolean => {
    return favoritesSet.has(upc);
  };

  return (
    <FavoritesContext.Provider
      value={{
        favoriteUPCs,
        isLoading,
        error: error as Error | null,
        isFavorite,
        refetch,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavoritesContext = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error(
      "useFavoritesContext must be used within a FavoritesProvider"
    );
  }
  return context;
};

// Safe version that returns default values when context is not available
export const useFavoritesContextSafe = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    return {
      favoriteUPCs: [],
      isLoading: false,
      error: null,
      isFavorite: () => false,
      refetch: () => {},
    };
  }
  return context;
};
